import { useMemo, useState, useEffect } from 'react';
import { useStatisticsContext } from '../../../../../providers/StatisticsProvider';
import { useOptionalStatisticsStatsState } from '../../../providers/StatisticsPageProvider';
import { useStatisticsCapabilities } from '../../../common/lib/statisticsAccess';
import { resolvePersistedChartKind } from '../lib/statisticsPersistedCharts';
import { aggregatePrimaryPhaseOrdersByDimension } from '../../pipeline/lib/aggregatePrimaryPhaseOrdersByDimension';
import { ORDER_PRIMARY_PHASE_DIMENSIONS } from '../../pipeline/lib/orderPrimaryPhaseDimension';
import { resolveDashboardBarDualPhaseMetric, resolveDashboardBarMetric, STATISTICS_DASHBOARD_VARIANT } from '../lib/dashboardPresetLayout';

/**
 * builtin 图展示态：live 读页面 statsState；frozen / 非跟页读 chartBundle。
 * 返回各子图共用的 data + handlers，避免在 JSX 里逐字段拆传。
 */
const useBuiltinChartDisplay = ({ snapshot, frozenBundle = null }) => {
	const statsState = useOptionalStatisticsStatsState();
	const { overdueData, isLoading: isOverdueLoading } = useStatisticsContext();
	const { dashboardVariant } = useStatisticsCapabilities();
	const usePageState = !frozenBundle && Boolean(statsState);
	const isInteractive = usePageState;
	const isSale = dashboardVariant === STATISTICS_DASHBOARD_VARIANT.SALE;
	const [localDimension, setLocalDimension] = useState(null);

	useEffect(() => {
		setLocalDimension(null);
	}, [snapshot?.id]);

	return useMemo(() => {
		const chartKind = resolvePersistedChartKind(snapshot);
		const animationKey = usePageState
			? statsState.chartAnimationKey
			: (frozenBundle?.animationKey ?? `pipeline-${snapshot?.id ?? 'chart'}`);

		const pipeline = {
			lensData: usePageState ? statsState.fullLensData : frozenBundle?.lensData,
			isLoading: usePageState ? statsState.isStatisticsQueryLoading : Boolean(frozenBundle?.chartLoading),
			selectedPhase: usePageState ? statsState.selectedPhase : (frozenBundle?.selectedPhase ?? null),
			onSelectPhase: isInteractive ? statsState.handleSelectPhase : undefined,
			animationKey,
			activeMetric: usePageState ? statsState.activeMetric : (frozenBundle?.activeMetric ?? statsState?.activeMetric),
			barMetric: resolveDashboardBarMetric(dashboardVariant, usePageState ? statsState.activeMetric : (frozenBundle?.activeMetric ?? statsState?.activeMetric)),
			barDualPhaseMetric: resolveDashboardBarDualPhaseMetric(dashboardVariant),
		};

		const bundleDimension = frozenBundle?.activeDimension ?? statsState?.activeDimension ?? ORDER_PRIMARY_PHASE_DIMENSIONS.SALE_USER;
		const activeDimension = usePageState ? statsState.activeDimension : (localDimension ?? bundleDimension);
		let dimensionRows = usePageState ? statsState.dimensionRows : (frozenBundle?.dimensionRows ?? []);
		if (!usePageState && activeDimension !== bundleDimension) {
			const aggregateRows = frozenBundle?.chartAggregates?.dimensions?.[activeDimension];
			if (Array.isArray(aggregateRows) && aggregateRows.length > 0) {
				dimensionRows = aggregateRows;
			} else if (Array.isArray(frozenBundle?.orders) && frozenBundle.orders.length > 0) {
				dimensionRows = aggregatePrimaryPhaseOrdersByDimension(frozenBundle.orders, activeDimension);
			}
		} else if (!usePageState) {
			const aggregateRows = frozenBundle?.chartAggregates?.dimensions?.[activeDimension];
			if (Array.isArray(aggregateRows) && aggregateRows.length > 0) {
				dimensionRows = aggregateRows;
			}
		}

		const dimension = {
			rows: dimensionRows,
			isLoading: pipeline.isLoading,
			activeDimension,
			onDimensionChange: isInteractive ? statsState.setActiveDimension : setLocalDimension,
			activeMetric: pipeline.activeMetric,
			selectedKey: isInteractive ? statsState.selectedDimensionKey : null,
			onSelectRow: isInteractive ? statsState.handleSelectDimension : undefined,
			animationKey,
		};

		const map = {
			orders: usePageState ? statsState.orders : (frozenBundle?.orders ?? []),
			geoAggregates: usePageState ? statsState.chartAggregates?.geo : frozenBundle?.chartAggregates?.geo,
			highlightProvinceNames: usePageState ? statsState.highlightProvinceNames : (frozenBundle?.highlightProvinceNames ?? []),
			highlightAnchorCityRows: usePageState ? statsState.highlightAnchorCityRows : (frozenBundle?.highlightAnchorCityRows ?? []),
			isLoading: pipeline.isLoading,
			animationKey,
			chartInstanceKeyPrefix: isInteractive ? 'dashboard-builtin-china-map' : 'dashboard-builtin-china-map-frozen',
		};

		const overdue = {
			data: usePageState ? overdueData : (frozenBundle?.overdueData ?? overdueData),
			isLoading: usePageState ? isOverdueLoading : Boolean(frozenBundle?.chartLoading ?? isOverdueLoading),
			lockDimension: isSale,
		};

		return { chartKind, isSale, isInteractive, pipeline, dimension, map, overdue };
	}, [
		dashboardVariant,
		frozenBundle,
		isInteractive,
		isOverdueLoading,
		isSale,
		localDimension,
		overdueData,
		snapshot,
		statsState,
		usePageState,
	]);
};

export default useBuiltinChartDisplay;
